/**
 * PRD-017 §3.14 attachments on a work item (Sean, Gallop - Builds 2026-09-15 03:14 / 03:16: "definitely need the
 * ability to drop documents into the mark done area of a task" → option A, Gallop-held storage; ADR 0034).
 *
 * One rule set shared by the Mark done dialog, the drawer's Attachments block, the API and the scan worker:
 *   - the type gate (declared type, file name and the first bytes must agree; macro-enabled Office types never),
 *   - the size cap (PRD-017 Q17, R0 default 25 MB),
 *   - the scan state machine (pending → clean | failed; nothing is served until clean),
 *   - the structural checks the platform's quarantine fence runs on PDF and Office files (active content),
 *   - the dev / test stub verdict (clean unless the EICAR signature or a structural flag is present).
 * Nothing here touches storage, the network or the database.
 */

export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024
export const ATTACHMENT_NAME_MAX = 255
/** Files per upload request from the browser (the dialog and the drawer send one request per file, but the cap holds either way). */
export const ATTACHMENTS_PER_ITEM_MAX = 50

export type AttachmentScanStatus = 'pending' | 'clean' | 'failed'

/** A verdict from any Scanner implementation (clamd, the fence, the stub). `reason` is a short machine token; `detail` is free text for the record, never shown to a client user. */
export interface ScanVerdict {
  readonly ok: boolean
  readonly engine: string
  readonly reason?: string
  readonly detail?: string
}

/**
 * The allowed declared MIME types (PRD-017 §3.14: images, PDF, CSV, xlsx). SVG is an image that carries script, so it
 * is not an image here. Any macro-enabled Office type (`.xlsm`, `.docm`, `application/vnd.ms-excel.sheet.macroEnabled.12`)
 * is refused by absence from this list, and the xlsx structural check refuses a macro project hidden under the xlsx type.
 * `application/json` (2026-09-19, skills 2 and 3): a structured export a skill reads as an input through a pinned schema
 * (`threads/attachmentSchemas.ts`: bank statement, cash count, POS closing report); text with no NUL, one object or array.
 * It is never previewed inline and carries no script surface (PRD-017 edge case 45 does not apply). Sticky: widening the
 * customer-facing type list; recorded in the skills 2/3 butterfly report, pending Sean.
 */
export const ATTACHMENT_ALLOWED_MIME: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
  'text/csv',
  'application/json',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
])

const EXT_TO_MIME: Readonly<Record<string, string>> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  pdf: 'application/pdf',
  csv: 'text/csv',
  json: 'application/json',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

/** The type Gallop stores for a file, from the extension when the browser's declared type is blank or generic (Safari sends `application/octet-stream` for CSV). */
export function attachmentMimeFor(name: string, declared: string | null | undefined): string | null {
  const ext = name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1]
  const fromExt = ext ? EXT_TO_MIME[ext] ?? null : null
  const d = (declared ?? '').split(';')[0]!.trim().toLowerCase()
  if (d && ATTACHMENT_ALLOWED_MIME.has(d)) {
    // The extension and the declared type must agree when both are known: "invoice.pdf" declared as image/png is refused.
    if (fromExt && fromExt !== d && !(fromExt.startsWith('image/') && d.startsWith('image/')))
      return null
    return d
  }
  if (!d || d === 'application/octet-stream' || d === 'application/vnd.ms-excel' || d === 'text/plain')
    return fromExt
  return null
}

export interface AttachmentInput {
  readonly name: string
  readonly mime: string | null | undefined
  readonly size: number
}

export const ATTACHMENT_ALLOWED_LABEL = 'images (PNG, JPEG, GIF, WebP, HEIC), PDF, CSV, JSON or Excel (.xlsx)'

/** Human message for the inline refusal / the 422, or null when the file passes the pre-upload gate (name, type, size). */
export function attachmentProblem(input: AttachmentInput): string | null {
  const name = input.name.trim()
  if (name.length === 0)
    return 'The file has no name.'
  if (name.length > ATTACHMENT_NAME_MAX)
    return `The file name is over ${ATTACHMENT_NAME_MAX} characters.`
  if (name.includes('/') || name.includes('\\') || name.includes('\0'))
    return 'The file name cannot contain a path.'
  if (input.size <= 0)
    return 'The file is empty.'
  if (input.size > ATTACHMENT_MAX_BYTES)
    return `${name} is ${formatBytes(input.size)}; the cap is ${formatBytes(ATTACHMENT_MAX_BYTES)} per file.`
  if (attachmentMimeFor(name, input.mime) === null)
    return `${name} is not an allowed type. Attach ${ATTACHMENT_ALLOWED_LABEL}.`
  return null
}

export function formatBytes(n: number): string {
  if (n < 1024)
    return `${n} B`
  if (n < 1024 * 1024)
    return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(n >= 10 * 1024 * 1024 ? 0 : 1)} MB`
}

// ---------------------------------------------------------------------------------------------
// Content sniffing: the first bytes must match the stored type. A PDF is served as a PDF, so a
// file that is not one must never be stored under that type.
// ---------------------------------------------------------------------------------------------

function startsWith(bytes: Uint8Array, sig: number[], offset = 0): boolean {
  if (bytes.length < offset + sig.length)
    return false
  return sig.every((b, i) => bytes[offset + i] === b)
}

function ascii(bytes: Uint8Array, from: number, len: number): string {
  let s = ''
  for (let i = from; i < Math.min(bytes.length, from + len); i++) s += String.fromCharCode(bytes[i]!)
  return s
}

/** True when the first bytes are what `mime` promises. CSV has no signature: it must be text with no NUL and no PDF / zip / image header. */
export function attachmentBytesMatch(bytes: Uint8Array, mime: string): boolean {
  switch (mime) {
    case 'image/png':
      return startsWith(bytes, [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A])
    case 'image/jpeg':
      return startsWith(bytes, [0xFF, 0xD8, 0xFF])
    case 'image/gif':
      return ascii(bytes, 0, 3) === 'GIF'
    case 'image/webp':
      return ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP'
    case 'image/heic':
    case 'image/heif':
      return ascii(bytes, 4, 4) === 'ftyp'
    case 'application/pdf':
      return ascii(bytes, 0, 5) === '%PDF-'
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return startsWith(bytes, [0x50, 0x4B, 0x03, 0x04])
    case 'text/csv': {
      if (ascii(bytes, 0, 5) === '%PDF-' || startsWith(bytes, [0x50, 0x4B]) || startsWith(bytes, [0x89, 0x50, 0x4E, 0x47]) || startsWith(bytes, [0xFF, 0xD8]))
        return false
      const probe = Math.min(bytes.length, 8192)
      for (let i = 0; i < probe; i++) {
        if (bytes[i] === 0)
          return false
      }
      return true
    }
    case 'application/json': {
      // Text with no NUL whose first non-whitespace byte opens an object or an array (a UTF-8 BOM is allowed).
      const probe = Math.min(bytes.length, 8192)
      let i = 0
      if (startsWith(bytes, [0xEF, 0xBB, 0xBF]))
        i = 3
      while (i < probe && (bytes[i] === 0x20 || bytes[i] === 0x09 || bytes[i] === 0x0A || bytes[i] === 0x0D)) i++
      if (i >= bytes.length || (bytes[i] !== 0x7B && bytes[i] !== 0x5B))
        return false
      for (let k = 0; k < probe; k++) {
        if (bytes[k] === 0)
          return false
      }
      return true
    }
    default:
      return false
  }
}

// ---------------------------------------------------------------------------------------------
// Structural checks (platform-architecture security posture; the fence's PDF / Office flags).
// ---------------------------------------------------------------------------------------------

/** Active-content markers in a PDF body. A match fails the scan: a count sheet never needs JavaScript, a launch action or an embedded file. */
const PDF_FLAGS: ReadonlyArray<{ token: string, reason: string }> = [
  { token: '/JavaScript', reason: 'pdf.javascript' },
  { token: '/JS ', reason: 'pdf.javascript' },
  { token: '/JS<', reason: 'pdf.javascript' },
  { token: '/JS/', reason: 'pdf.javascript' },
  { token: '/Launch', reason: 'pdf.launch' },
  { token: '/EmbeddedFile', reason: 'pdf.embedded_file' },
  { token: '/OpenAction', reason: 'pdf.open_action' },
  { token: '/AA', reason: 'pdf.additional_action' },
]

/** File names inside an OOXML zip that mean a macro project or an external template reference. */
const OOXML_FLAGS: ReadonlyArray<{ token: string, reason: string }> = [
  { token: 'vbaProject.bin', reason: 'office.macro_project' },
  { token: 'vbaData.xml', reason: 'office.macro_project' },
  { token: 'attachedTemplate', reason: 'office.remote_template' },
  { token: 'oleObject', reason: 'office.ole_object' },
]

function latin1(bytes: Uint8Array): string {
  // Latin-1 keeps one char per byte so token offsets stay honest; 25 MB fits comfortably.
  let out = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK)
    out += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)))
  return out
}

/** The structural verdict for a type that has one; `null` for images and CSV (nothing to check). */
export function attachmentStructuralProblem(bytes: Uint8Array, mime: string): { reason: string, detail: string } | null {
  if (mime === 'application/pdf') {
    const body = latin1(bytes)
    for (const f of PDF_FLAGS) {
      if (body.includes(f.token))
        return { reason: f.reason, detail: `PDF contains ${f.token.trim()}` }
    }
    return null
  }
  if (mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
    // Zip entry names are stored uncompressed in the local headers and the central directory, so a plain search finds them.
    const body = latin1(bytes)
    for (const f of OOXML_FLAGS) {
      if (body.includes(f.token))
        return { reason: f.reason, detail: `Workbook contains ${f.token}` }
    }
    return null
  }
  return null
}

// ---------------------------------------------------------------------------------------------
// Scan state machine and the stub scanner.
// ---------------------------------------------------------------------------------------------

/** pending → clean | failed. Clean and failed are final: a re-scan needs a new upload (PRD-017 edge case 44). */
export function scanTransition(from: AttachmentScanStatus, verdict: ScanVerdict): AttachmentScanStatus | null {
  if (from !== 'pending')
    return null
  return verdict.ok ? 'clean' : 'failed'
}

/** The server-side serve gate: only a clean, undeleted row's bytes ever leave the API. */
export function canServeAttachment(row: { scanStatus: AttachmentScanStatus, deletedAt: string | Date | null }): boolean {
  return row.scanStatus === 'clean' && row.deletedAt === null
}

/**
 * The EICAR test signature (eicar.org): every antivirus engine flags it and it is harmless. The stub fails a file
 * containing it so the "failed" path is exercised end to end without malware in the repo. Split so this source file
 * does not itself trip a scanner.
 */
export const EICAR_SIGNATURE = ['X5O!P%@AP[4\\PZX54(P^)7CC)7}$EICAR', '-STANDARD-ANTIVIRUS-TEST-FILE!$H+H*'].join('')

/**
 * The dev / test verdict: the structural checks, then the EICAR signature. Used by the `stub` Scanner (never in a
 * deployed environment: the API refuses to start with it under NODE_ENV=production) and as the pre-check the real
 * scanner runs before handing bytes to clamd.
 */
export function stubScanVerdict(bytes: Uint8Array, mime: string): ScanVerdict {
  const structural = attachmentStructuralProblem(bytes, mime)
  if (structural)
    return { ok: false, engine: 'structural', reason: structural.reason, detail: structural.detail }
  if (latin1(bytes).includes(EICAR_SIGNATURE))
    return { ok: false, engine: 'stub', reason: 'signature.eicar', detail: 'EICAR test signature' }
  return { ok: true, engine: 'stub' }
}

/** Status chip words (one vocabulary for the drawer, the dialog and the Close tab list). */
export const SCAN_STATUS_LABEL: Readonly<Record<AttachmentScanStatus, string>> = { pending: 'Scanning', clean: 'Clean', failed: 'Failed' }

/** Whether the browser can render an inline preview once the file is clean (images and PDF, PRD-017 §3.14). */
export function attachmentPreviewKind(mime: string): 'image' | 'pdf' | null {
  if (mime === 'application/pdf')
    return 'pdf'
  if (mime === 'image/png' || mime === 'image/jpeg' || mime === 'image/gif' || mime === 'image/webp')
    return 'image'
  return null
}
